import { getPrinterCapabilities, isChromiumBrowser } from './platform';
import type { PrintResult, ReceiptPayload, PrinterConnectionState, BillPrintResult } from './types';
import {
  encodeIsscXpP203aTestReceipt,
  encodeIsscXpP203aTestReceiptWithCut,
  encodePlainTextNotes,
  encodeReceipt,
  encodeXpP203aTestReceipt,
} from './escpos/encodeReceipt';
import { resolveXpP203aEscPosProfile } from './resolveXpP203aEscPosProfile';
import { getReceiptLineWidthForModel } from './receiptLayout';
import { getEffectivePrinterLinkSettings } from './printerModelConfig';
import { buildPostPrintDebugReport, type PostPrintDebugReport } from './printerHelpDebug';
import { previewEscPosBytes, previewPayloadBytes, printerDebug, printerDebugWarn } from './printerDebug';
import {
  encodeXprinterSetAutoPowerOffMinutes,
  encodeXprinterWakeBytes,
} from './escpos/xprinterVendorCommands';
import { concatBytes } from './escpos/commands';
import { getPrinterPowerSettings } from './printerPowerSettings';
import { startBleKeepAlive, stopBleKeepAlive } from './bleConnectionKeepAlive';
import {
  encodeReceiptToXpP203aTspl,
  encodeXpP203aTsplConnectProbe,
  encodeXpP203aTsplTestPrint,
} from './tspl/xpP203aTsplPrint';
import { previewTsplBytes } from './tspl/tsplEncode';
import { getXpP203aPrintMode, shouldUseXpP203aTspl } from './xpP203aLabelConfig';
import {
  getXpP203aBluetoothTransport,
  xpP203aConnectModeFromTransport,
} from './xpP203aBluetoothTransport';
import { WebBluetoothTransport, type BleTransportDiagnostics } from './transports/WebBluetoothTransport';
import { WebSerialTransport, type WebSerialConnectionKind, type WebSerialTransportOptions } from './transports/WebSerialTransport';
import type { PrinterTransport } from './transports/PrinterTransport';
import { nativeBtpPrinterAdapter } from './native-btp/NativeBtpPrinterAdapter';
import { PayzheBtpError } from './native-btp/PayzheBtpError';
import {
  payzheBtpProvider,
  subscribePayzheBtpProvider,
} from './native-btp/PayzheBtpProvider';
import {
  resolveInitialRoute,
  shouldFallbackToBrowserOnNativeFailure,
  shouldUsePayzheBtpNative,
} from './native-btp/resolveBluetoothProvider';
import { isPayzheBtpEligible } from './native-btp/mobilePlatform';

export type PrinterConnectMode = 'ble' | 'serial' | 'bluetooth-serial';

/**
 * Web-only facade for thermal printing (Web Bluetooth / Web Serial).
 * Must call connectPrinter from a user gesture for browser security.
 */
export type LastPrintSnapshot = {
  path: string;
  byteLength: number;
  preview: string;
  at: number;
  bytes?: Uint8Array;
};

export type { PostPrintDebugReport };

type PrintDebugListener = (report: PostPrintDebugReport) => void;

export class BluetoothPrinterService {
  private transport: PrinterTransport | null = null;

  private activeProvider: 'native' | 'browser' | null = null;

  private lastPrintSnapshot: LastPrintSnapshot | null = null;

  private lastPostPrintDebug: PostPrintDebugReport | null = null;

  private readonly printDebugListeners = new Set<PrintDebugListener>();

  private readonly listeners = new Set<() => void>();

  private xpP203aStatusMessageKey: string | null = null;

  private printWriteInFlight = false;

  constructor() {
    if (isPayzheBtpEligible()) {
      subscribePayzheBtpProvider(() => {
        const snap = payzheBtpProvider.getSnapshot();
        if (
          snap.status?.connected &&
          shouldUsePayzheBtpNative() &&
          resolveInitialRoute() === 'native'
        ) {
          this.activeProvider = 'native';
        }
        if (this.activeProvider === 'native') {
          this.notify();
        }
      });
    }
  }

  getActiveProvider(): 'native' | 'browser' | null {
    return this.activeProvider;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    Array.from(this.listeners).forEach((fn) => {
      try {
        fn();
      } catch {
        /* ignore subscriber errors */
      }
    });
  }

  ping(): void {
    this.notify();
  }

  /** Align web printer facade with payzhe BTP when the native app already has a connection. */
  async syncNativeProviderFromBridge(): Promise<void> {
    if (!isPayzheBtpEligible() || !shouldUsePayzheBtpNative()) return;
    const snap = payzheBtpProvider.getSnapshot();
    if (!snap.status?.connected) return;
    await this.disconnectBrowserTransport();
    this.activeProvider = 'native';
    if (snap.status.transport === 'ble') {
      this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedBle';
    } else if (
      snap.status.transport === 'classic-serial' ||
      snap.status.transport === 'bluetooth-serial'
    ) {
      this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedRfcomm';
    }
    this.notify();
  }

  getCapabilities() {
    return getPrinterCapabilities();
  }

  getXpP203aStatusMessageKey(): string | null {
    return this.xpP203aStatusMessageKey;
  }

  getConnectionState(): PrinterConnectionState {
    if (this.activeProvider === 'native') {
      return nativeBtpPrinterAdapter.getConnectionState();
    }
    return this.transport?.getState() ?? 'disconnected';
  }

  getActiveMode(): 'ble' | 'serial' | null {
    if (this.activeProvider === 'native') {
      const transport = payzheBtpProvider.getSnapshot().status?.transport;
      if (transport === 'ble') return 'ble';
      if (transport === 'classic-serial' || transport === 'bluetooth-serial') return 'serial';
      return 'serial';
    }
    if (!this.transport) return null;
    const k = this.transport.kind;
    if (k === 'ble' || k === 'serial') return k;
    return null;
  }

  /** Web Serial connected over Bluetooth RFCOMM (SPP), not USB. */
  isBluetoothSerialConnection(): boolean {
    if (this.activeProvider === 'native') {
      const transport = payzheBtpProvider.getSnapshot().status?.transport;
      return transport === 'classic-serial' || transport === 'bluetooth-serial';
    }
    const t = this.transport;
    return t?.kind === 'serial' && Boolean(t.isBluetoothSpp?.());
  }

  isConnected(): boolean {
    if (this.activeProvider === 'native') {
      return nativeBtpPrinterAdapter.isConnected();
    }
    const s = this.getConnectionState();
    return s === 'connected' || s === 'printing';
  }

  isPrintWriteInFlight(): boolean {
    return this.printWriteInFlight;
  }

  /** Send vendor control bytes (power settings, keep-alive) without print debug noise. */
  async writeControlBytes(bytes: Uint8Array): Promise<void> {
    if (this.activeProvider === 'native') {
      await nativeBtpPrinterAdapter.print(bytes, { requestId: `control-${Date.now()}` });
      return;
    }
    if (!this.transport || this.transport.kind === 'unsupported') {
      throw new Error('bluetoothPrinter.errors.notConnected');
    }
    if (this.printWriteInFlight) {
      return;
    }
    const transport = this.transport;
    if (typeof transport.writeAuxiliary === 'function') {
      await transport.writeAuxiliary(bytes);
      return;
    }
    await transport.write(bytes);
  }

  async applyPrinterAutoPowerOff(minutes?: number): Promise<void> {
    const settings = getPrinterPowerSettings();
    const targetMinutes = minutes ?? settings.autoPowerOffMinutes;
    const command = encodeXprinterSetAutoPowerOffMinutes(targetMinutes);
    const link = getEffectivePrinterLinkSettings();

    let payload = command;
    if (this.getActiveMode() === 'ble' && link.modelId === 'xp-p203a') {
      payload = concatBytes(encodeXprinterWakeBytes(), command);
    }

    printerDebug('Apply printer auto power-off', {
      minutes: targetMinutes,
      byteLength: payload.length,
      modelId: link.modelId,
      transport: this.getActiveMode(),
    });
    await this.writeControlBytes(payload);
  }

  private async applyPrinterPowerSettingsOnConnect(): Promise<void> {
    const settings = getPrinterPowerSettings();
    if (!settings.applyOnConnect) return;
    try {
      await this.applyPrinterAutoPowerOff(settings.autoPowerOffMinutes);
      printerDebug('Printer auto power-off applied on connect', {
        minutes: settings.autoPowerOffMinutes,
      });
    } catch (e) {
      printerDebugWarn('Printer auto power-off on connect failed (non-fatal)', e);
    }
  }

  private startBleKeepAliveIfNeeded(mode: PrinterConnectMode): void {
    if (mode !== 'ble') return;
    if (!getPrinterPowerSettings().bleKeepAliveEnabled) return;
    startBleKeepAlive(this);
  }

  /** Last BLE GATT write channel diagnostics (after connect / last write). */
  getBleDiagnostics(): BleTransportDiagnostics | null {
    const t = this.transport;
    if (t?.kind !== 'ble') return null;
    return (t as WebBluetoothTransport).getDiagnostics();
  }

  private resolveXpP203aProfile() {
    return resolveXpP203aEscPosProfile({
      transportKind: this.getActiveMode(),
      bleDiagnostics: this.getBleDiagnostics(),
    });
  }

  getLastPrintSnapshot(): LastPrintSnapshot | null {
    return this.lastPrintSnapshot;
  }

  getLastPostPrintDebug(): PostPrintDebugReport | null {
    return this.lastPostPrintDebug;
  }

  /** Admin UI: called after every print attempt with logs + HEX bundle. */
  subscribePrintDebug(listener: PrintDebugListener): () => void {
    this.printDebugListeners.add(listener);
    return () => {
      this.printDebugListeners.delete(listener);
    };
  }

  private recordPrintSnapshot(path: string, bytes: Uint8Array, useTsplPreview = false): void {
    this.lastPrintSnapshot = {
      path,
      byteLength: bytes.length,
      preview: useTsplPreview ? previewTsplBytes(bytes) : previewPayloadBytes(bytes),
      at: Date.now(),
      bytes: bytes.slice(),
    };
  }

  private buildWebSerialOptions(
    link: ReturnType<typeof getEffectivePrinterLinkSettings>,
    connectionKind: WebSerialConnectionKind,
  ): WebSerialTransportOptions {
    const useTspl = shouldUseXpP203aTspl(link.modelId);
    return {
      baudRate: link.serialBaud,
      connectionKind,
      singleShotWrite: link.modelId === 'xp-p203a',
      connectProbeBytes: useTspl ? encodeXpP203aTsplConnectProbe() : undefined,
      xpP203aLogging: link.modelId === 'xp-p203a',
    };
  }

  private logXpP203aPayload(bytes: Uint8Array, label: string): void {
    console.log(`[XP-P203A] ${label}: ${bytes.length} bytes`);
    console.log(`[XP-P203A] Raw command preview: ${previewTsplBytes(bytes, 500)}`);
  }

  private logXpP203aWriteContext(bytes: Uint8Array): void {
    const link = getEffectivePrinterLinkSettings();
    if (link.modelId !== 'xp-p203a') return;

    const mode = getXpP203aPrintMode();
    const transport = this.getActiveMode() ?? 'none';
    const serialKind =
      this.transport?.kind === 'serial' && this.isBluetoothSerialConnection()
        ? 'bluetooth-spp'
        : this.transport?.kind === 'serial'
          ? 'usb'
          : undefined;
    const payloadKind = bytes[0] === 0x1b || bytes[0] === 0x1d ? 'ESC/POS' : 'TSPL';
    const baudPart = transport === 'serial' ? ` baud=${link.serialBaud}` : '';

    console.log(
      `[XP-P203A] Write context: mode=${mode} transport=${transport}${serialKind ? ` (${serialKind})` : ''}${baudPart} payload=${payloadKind}`,
    );

    if (mode === 'label' && payloadKind === 'ESC/POS') {
      printerDebugWarn(
        'XP-P203A label mode expects TSPL; payload looks like ESC/POS — check Print mode on /thermal-printer',
      );
    }
  }

  private emitPostPrintDebug(path: string, ok: boolean, error?: string, escPosBytes?: Uint8Array): void {
    const link = getEffectivePrinterLinkSettings();
    const report = buildPostPrintDebugReport(this, {
      path,
      ok,
      error,
      escPosBytes: escPosBytes ?? this.lastPrintSnapshot?.bytes,
      modelId: link.modelId,
      bridgeProfile: this.getBleDiagnostics()?.bridgeProfile ?? null,
    });
    this.lastPostPrintDebug = report;
    for (const fn of this.printDebugListeners) {
      try {
        fn(report);
      } catch {
        /* ignore subscriber errors */
      }
    }
  }

  async connectPrinter(mode: PrinterConnectMode, printerId?: string): Promise<void> {
    const route = resolveInitialRoute();
    if (route === 'native' && shouldUsePayzheBtpNative()) {
      try {
        await this.connectViaNative(mode, printerId);
        return;
      } catch (error) {
        if (!shouldFallbackToBrowserOnNativeFailure()) {
          throw error instanceof PayzheBtpError
            ? error
            : error instanceof Error
              ? error
              : new Error(String(error));
        }
        printerDebugWarn('payzhe BTP connect failed, falling back to browser', error);
      }
    }
    await this.connectViaBrowser(mode);
  }

  private async connectViaNative(mode: PrinterConnectMode, printerId?: string): Promise<void> {
    this.xpP203aStatusMessageKey = null;
    await this.disconnectBrowserTransport();
    this.activeProvider = 'native';
    this.notify();
    try {
      await nativeBtpPrinterAdapter.connect(mode, printerId);
      const snap = payzheBtpProvider.getSnapshot().status;
      if (snap?.transport === 'ble') {
        this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedBle';
      } else if (
        snap?.transport === 'classic-serial' ||
        snap?.transport === 'bluetooth-serial'
      ) {
        this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedRfcomm';
      }
      this.notify();
    } catch (error) {
      this.activeProvider = null;
      this.notify();
      throw error;
    }
  }

  private async connectViaBrowser(mode: PrinterConnectMode): Promise<void> {
    const caps = getPrinterCapabilities();
    if (!caps.canPrint) {
      throw new Error(caps.unsupportedReason ?? 'bluetoothPrinter.errors.unsupportedPlatform');
    }
    if (mode === 'ble' && !caps.webBluetooth) {
      throw new Error('bluetoothPrinter.errors.noWebBluetooth');
    }
    if ((mode === 'serial' || mode === 'bluetooth-serial') && !caps.webSerial) {
      throw new Error('bluetoothPrinter.errors.noWebSerial');
    }
    if (mode === 'ble' && caps.requiresChromium && !isChromiumBrowser()) {
      printerDebugWarn('Web Bluetooth works best in Chromium (Chrome / Edge)');
    }

    this.xpP203aStatusMessageKey = null;
    await this.disconnectPrinter();

    const onTransportState = () => this.notify();

    const link = getEffectivePrinterLinkSettings();

    let next: PrinterTransport;
    if (mode === 'bluetooth-serial') {
      const serialOpts = this.buildWebSerialOptions(link, 'bluetooth-spp');
      next = new WebSerialTransport({
        onStateChange: onTransportState,
        ...serialOpts,
      });
      printerDebug('Connecting XP-P203A via Web Serial Bluetooth RFCOMM (SPP)', {
        baudRate: serialOpts.baudRate,
        singleShotWrite: serialOpts.singleShotWrite,
        tsplLabelMode: shouldUseXpP203aTspl(link.modelId),
      });
    } else if (mode === 'serial') {
      next = new WebSerialTransport({
        onStateChange: onTransportState,
        ...this.buildWebSerialOptions(link, 'usb'),
      });
    } else {
      next = new WebBluetoothTransport({
        onStateChange: onTransportState,
        onGattServerDisconnected: () => {
          stopBleKeepAlive();
          if (this.transport === next) {
            this.transport = null;
          }
          this.notify();
        },
        bleChunkBytes: link.bleChunkBytes,
        bleInterChunkDelayMs: link.bleInterChunkDelayMs,
        blePreferWriteWithResponse: link.bleWriteWithResponse,
        blePostWriteSettleMs: link.blePostWriteSettleMs,
        printerModelId: link.modelId,
      });
    }

    this.transport = next;
    this.activeProvider = 'browser';
    this.notify();

    try {
      await next.connect();
    } catch (e) {
      this.transport = null;
      this.activeProvider = null;
      this.notify();
      throw e instanceof Error ? e : new Error(String(e));
    }

    if (
      mode === 'ble' &&
      link.modelId === 'xp-p203a' &&
      shouldUseXpP203aTspl(link.modelId) &&
      next instanceof WebBluetoothTransport
    ) {
      try {
        const probe = encodeXpP203aTsplConnectProbe();
        printerDebug('BLE TSPL connect probe', {
          byteLength: probe.length,
          preview: previewTsplBytes(probe),
        });
        await next.write(probe);
        console.log('[XP-P203A] BLE TSPL connect probe sent');
      } catch (probeErr) {
        printerDebugWarn('BLE TSPL connect probe failed', probeErr);
      }
    }

    if (mode === 'ble' && link.modelId === 'xp-p203a' && next instanceof WebBluetoothTransport) {
      const diag = next.getDiagnostics();
      printerDebug('BLE connect summary (XP-P203A)', {
        serviceUuid: diag?.serviceUuid,
        characteristicUuid: diag?.characteristicUuid,
        bridgeProfile: diag?.bridgeProfile,
        wakeOnWrite: diag?.wakeOnWrite,
        isscNotifyEnabled: diag?.isscNotifyEnabled,
        explicitUuidFallback: diag?.explicitUuidFallback,
        writeWithoutResponseAvailable: diag?.writeWithoutResponseAvailable,
        chunkBytes: diag?.chunkBytes,
        interChunkDelayMs: diag?.interChunkDelayMs,
        postWriteSettleMs: diag?.postWriteSettleMs,
        writeMode: diag?.writeMode,
      });
      console.log('[XP-P203A] BLE connected');
      if (diag?.bridgeProfile === 'issc') {
        console.log('[XP-P203A] ISSC profile detected');
      }
      this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedBle';
    }

    if (mode === 'bluetooth-serial' && link.modelId === 'xp-p203a') {
      this.xpP203aStatusMessageKey = 'bluetoothPrinter.xpP203aConnectedRfcomm';
      this.notify();
    }

    await this.applyPrinterPowerSettingsOnConnect();
    this.startBleKeepAliveIfNeeded(mode);

    this.notify();
  }

  private async disconnectBrowserTransport(): Promise<void> {
    stopBleKeepAlive();
    if (this.transport) {
      await this.transport.disconnect().catch(() => {});
      this.transport = null;
    }
  }

  async disconnectPrinter(): Promise<void> {
    if (this.activeProvider === 'native') {
      await nativeBtpPrinterAdapter.disconnect().catch(() => {});
      this.activeProvider = null;
    }
    await this.disconnectBrowserTransport();
    this.notify();
  }

  /**
   * Write bytes with one retry; if GATT dropped, clears transport (reconnect needs user gesture).
   */
  private async writeWithRetry(
    bytes: Uint8Array,
    options?: { requestId?: string },
  ): Promise<void> {
    if (this.activeProvider === 'native') {
      this.printWriteInFlight = true;
      try {
        await nativeBtpPrinterAdapter.print(bytes, { requestId: options?.requestId });
      } finally {
        this.printWriteInFlight = false;
      }
      return;
    }

    if (!this.transport || this.transport.kind === 'unsupported') {
      throw new Error('bluetoothPrinter.errors.notConnected');
    }

    const attempt = async (): Promise<void> => {
      const link = getEffectivePrinterLinkSettings();
      if (link.modelId === 'xp-p203a') {
        this.logXpP203aWriteContext(bytes);
        if (shouldUseXpP203aTspl(link.modelId)) {
          this.logXpP203aPayload(bytes, 'TSPL payload');
        } else {
          console.log(`[XP-P203A] ESC/POS bytes: ${bytes.length}`);
        }
      }
      await this.transport!.write(bytes);
    };

    this.printWriteInFlight = true;
    try {
      try {
        await attempt();
        return;
      } catch (error_) {
        printerDebugWarn('Print write failed, retrying once', error_);
      }

      if (!this.isConnected()) {
        this.transport = null;
        this.notify();
        throw new Error('bluetoothPrinter.errors.notConnected');
      }

      try {
        await attempt();
      } catch (error_) {
        if (!this.isConnected()) {
          this.transport = null;
          this.notify();
        }
        throw error_;
      }
    } finally {
      this.printWriteInFlight = false;
    }
  }

  async printReceipt(
    receipt: ReceiptPayload,
    options?: { requestId?: string },
  ): Promise<PrintResult> {
    if (this.activeProvider === 'native' && !nativeBtpPrinterAdapter.isConnected()) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    if (this.activeProvider !== 'native' && (!this.transport || this.transport.kind === 'unsupported')) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    let bytes: Uint8Array | undefined;
    try {
      const link = getEffectivePrinterLinkSettings();
      const useTspl = shouldUseXpP203aTspl(link.modelId);
      if (useTspl) {
        bytes = await encodeReceiptToXpP203aTspl(receipt);
        this.recordPrintSnapshot('printReceipt', bytes, true);
        printerDebug('printReceipt (TSPL)', {
          path: 'printReceipt',
          modelId: link.modelId,
          byteLength: bytes.length,
          preview: previewTsplBytes(bytes),
        });
      } else {
        const profile =
          link.modelId === 'xp-p203a' ? this.resolveXpP203aProfile() : 'default';
        const lineWidth = getReceiptLineWidthForModel(link.modelId);
        bytes = await encodeReceipt(receipt, {
          lineWidth,
          profile,
          printerModelId: link.modelId,
        });
        this.recordPrintSnapshot('printReceipt', bytes);
        printerDebug('printReceipt', {
          path: 'printReceipt',
          modelId: link.modelId,
          profile,
          lineWidth,
          byteLength: bytes.length,
          preview: previewEscPosBytes(bytes),
          hasLogo: Boolean(receipt.brandingHeader?.logoUrl),
          bleDiagnostics: this.getBleDiagnostics(),
        });
      }
      await this.writeWithRetry(bytes, { requestId: options?.requestId });
      this.notify();
      this.emitPostPrintDebug('printReceipt', true, undefined, bytes);
      return { ok: true };
    } catch (e) {
      const msg =
        e instanceof PayzheBtpError
          ? e.errorKey ?? e.message
          : e instanceof Error
            ? e.message
            : String(e);
      this.notify();
      this.emitPostPrintDebug('printReceipt', false, msg, bytes);
      return { ok: false, error: msg };
    }
  }

  async printRaw(bytes: Uint8Array): Promise<PrintResult> {
    if (this.activeProvider === 'native' && !nativeBtpPrinterAdapter.isConnected()) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    if (this.activeProvider !== 'native' && (!this.transport || this.transport.kind === 'unsupported')) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    const payload = bytes.slice();
    try {
      this.recordPrintSnapshot('printRaw', payload);
      printerDebug('printRaw', { byteLength: payload.length, preview: previewEscPosBytes(payload) });
      await this.writeWithRetry(payload);
      this.notify();
      this.emitPostPrintDebug('printRaw', true, undefined, payload);
      return { ok: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.notify();
      this.emitPostPrintDebug('printRaw', false, msg, payload);
      return { ok: false, error: msg };
    }
  }

  /**
   * Test print: XP-P203A LABEL mode uses a minimal TSPL job; other models use ESC/POS notes.
   */
  async printTestReceipt(): Promise<PrintResult> {
    if (this.activeProvider === 'native' && !nativeBtpPrinterAdapter.isConnected()) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    if (this.activeProvider !== 'native' && (!this.transport || this.transport.kind === 'unsupported')) {
      return { ok: false, error: 'bluetoothPrinter.errors.notConnected' };
    }
    let bytes: Uint8Array | undefined;
    try {
      const link = getEffectivePrinterLinkSettings();
      const useTspl = shouldUseXpP203aTspl(link.modelId);
      if (useTspl) {
        bytes = encodeXpP203aTsplTestPrint();
        this.recordPrintSnapshot('printTestReceipt', bytes, true);
        printerDebug('printTestReceipt (TSPL)', {
          path: 'printTestReceipt',
          modelId: link.modelId,
          byteLength: bytes.length,
          preview: previewTsplBytes(bytes),
        });
        this.logXpP203aPayload(bytes, 'TSPL test job');
        try {
          await this.writeWithRetry(bytes);
        } catch {
          throw new Error('bluetoothPrinter.errors.tsplPrintFailed');
        }
      } else if (link.modelId === 'xp-p203a') {
        const profile = this.resolveXpP203aProfile();
        if (profile === 'issc-cut') {
          bytes = encodeIsscXpP203aTestReceiptWithCut();
        } else if (profile === 'issc') {
          bytes = encodeIsscXpP203aTestReceipt();
        } else {
          bytes = await encodeXpP203aTestReceipt();
        }
        this.recordPrintSnapshot('printTestReceipt', bytes);
        printerDebug('printTestReceipt', {
          path: 'printTestReceipt',
          modelId: link.modelId,
          byteLength: bytes.length,
          preview: previewEscPosBytes(bytes),
          bleDiagnostics: this.getBleDiagnostics(),
        });
        await this.writeWithRetry(bytes);
      } else {
        bytes = await encodePlainTextNotes(
          [
            '----------------',
            'PRINTER TEST',
            'Bluetooth OK',
            'ESC/POS OK',
            '----------------',
          ],
          'default',
          link.modelId,
        );
        this.recordPrintSnapshot('printTestReceipt', bytes);
        printerDebug('printTestReceipt', {
          path: 'printTestReceipt',
          modelId: link.modelId,
          byteLength: bytes.length,
          preview: previewEscPosBytes(bytes),
          bleDiagnostics: this.getBleDiagnostics(),
        });
        await this.writeWithRetry(bytes);
      }
      this.notify();
      this.emitPostPrintDebug('printTestReceipt', true, undefined, bytes);
      return { ok: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.notify();
      this.emitPostPrintDebug('printTestReceipt', false, msg, bytes);
      return { ok: false, error: msg };
    }
  }

  printReceiptAuto(receipt: ReceiptPayload): void {
    void this.printReceipt(receipt).then((res) => {
      if (!res.ok) {
        console.warn('[BluetoothPrinter]', res.error);
      }
    });
  }

  async printBillReceipt(
    receipt: ReceiptPayload,
    options?: { requestId?: string; wakeApp?: boolean | 'auto' },
  ): Promise<BillPrintResult> {
    const caps = this.getCapabilities();
    if (!caps.canPrint) {
      return { ok: true, printed: false, skipped: true };
    }

    const onAndroidNative = isPayzheBtpEligible() && shouldUsePayzheBtpNative();

    if (!onAndroidNative) {
      if (!this.isConnected()) {
        return { ok: true, printed: false, skipped: true };
      }
      const res = await this.printReceipt(receipt, { requestId: options?.requestId });
      return {
        ok: res.ok,
        printed: res.ok,
        skipped: false,
        error: res.ok ? undefined : res.error,
      };
    }

    try {
      await payzheBtpProvider.ensureReady({ wakeApp: options?.wakeApp ?? 'auto' });
    } catch (error) {
      const errorKey =
        error instanceof PayzheBtpError
          ? error.errorKey ?? error.message
          : 'bluetoothPrinter.payzheBtp.errors.unreachable';
      return { ok: false, printed: false, skipped: false, error: errorKey };
    }

    if (!nativeBtpPrinterAdapter.isConnected()) {
      await this.syncNativeProviderFromBridge();
    }

    if (!nativeBtpPrinterAdapter.isConnected()) {
      try {
        const mode = xpP203aConnectModeFromTransport(getXpP203aBluetoothTransport());
        await this.connectViaNative(mode);
      } catch (error) {
        const errorKey =
          error instanceof PayzheBtpError
            ? error.errorKey ?? error.message
            : 'bluetoothPrinter.payzheBtp.errors.connectFailed';
        return { ok: false, printed: false, skipped: false, error: errorKey };
      }
    }

    const res = await this.printReceipt(receipt, { requestId: options?.requestId });
    return {
      ok: res.ok,
      printed: res.ok,
      skipped: false,
      error: res.ok ? undefined : res.error,
    };
  }

  async printReceiptIfConnected(receipt: ReceiptPayload): Promise<{
    skipped: boolean;
    ok: boolean;
    error?: string;
  }> {
    if (!this.isConnected()) {
      return { skipped: true, ok: true };
    }
    const res = await this.printReceipt(receipt);
    if (res.ok) {
      return { skipped: false, ok: true };
    }
    return { skipped: false, ok: false, error: res.error };
  }
}

export const bluetoothPrinterService = new BluetoothPrinterService();
